import React, { useState } from 'react';
import { 
  BrainCircuit, 
  Network, 
  Radio, 
  Cpu, 
  Rocket, 
  Shield,
  ArrowRight,
  Globe,
  GitMerge,
  Target,
  AlertTriangle,
  FileText,
  Command,
  CheckCircle2,
  Building,
  Scale
} from 'lucide-react';

// --- Jargon Tooltip Logic ---
const jargonDict = {
  "JADC2": "Joint All-Domain Command and Control: DoD initiative to connect sensors from all military services into a single network.",
  "CJADC2": "Combined JADC2: The extension of the JADC2 network to include allied and partner nations.",
  "EW": "Electronic Warfare: Controlling the electromagnetic spectrum to attack or impede enemy operations.",
  "C-UAS": "Counter-UAS: Systems designed to detect, track, and destroy enemy drones.",
  "PoR": "Program of Record: A finalized, long-term DoD budget allocation ensuring recurring revenue.",
  "CFIUS": "Committee on Foreign Investment in the United States: Interagency committee that reviews foreign acquisitions for national security risks.",
  "BAA": "Buy American Act: Requires the U.S. government to prefer U.S.-made products in its purchases.",
  "DFARS": "Defense Federal Acquisition Regulation Supplement: Specific DoD acquisition rules. 'Qualifying Countries' have reciprocal agreements exempting them from certain BAA rules.",
  "FOCI": "Foreign Ownership, Control or Influence: A status requiring mitigation (like an SSA) before a foreign-owned U.S. entity can access classified information.",
  "SSA": "Special Security Agreement: A FOCI mitigation agreement allowing a foreign-owned U.S. company to operate on classified contracts by limiting the foreign parent's control.",
  "CMMC": "Cybersecurity Maturity Model Certification: DoD's unified standard for implementing cybersecurity across the defense industrial base.",
  "IL5/IL6": "Impact Level 5/6: Stringent DoD cloud security requirements for handling highly sensitive or classified national security systems.",
  "Replicator 2.0": "DoD initiative focused on scaling counter-small uncrewed aerial systems (C-sUAS) to protect critical installations."
};

const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const sortedJargonKeys = Object.keys(jargonDict).sort((a, b) => b.length - a.length);
const jargonRegex = new RegExp("(^|\\s|[([{])(" + sortedJargonKeys.map(escapeRegExp).join('|') + ")(?=\\s|$|[,.)}\\]s:/])", "g");

const renderWithJargon = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  const parts = text.split(jargonRegex);
  
  return parts.map((part, i) => {
    if (part && jargonDict[part]) {
      const splitPoint = jargonDict[part].indexOf(':');
      const title = splitPoint > -1 ? jargonDict[part].slice(0, splitPoint) : part;
      const desc = splitPoint > -1 ? jargonDict[part].slice(splitPoint + 1).trim() : jargonDict[part];
      
      return (
        <span key={i} className="group/jargon relative inline-block border-b border-dotted border-slate-500 cursor-help text-slate-200 hover:text-white transition-colors">
          {part}
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-[#0B1120] text-slate-300 text-[11px] leading-relaxed rounded-xl border border-slate-700 shadow-[0_15px_40px_rgba(0,0,0,0.9)] opacity-0 invisible group-hover/jargon:opacity-100 group-hover/jargon:visible transition-all z-[100] pointer-events-none font-sans font-normal normal-case text-left tracking-normal">
            <span className="font-bold text-cyan-400 block mb-1">{title}</span>
            <span className="text-slate-400">{desc}</span>
          </span>
        </span>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

export default function AlliedMarketEntryDossier() {
  const [activeTab, setActiveTab] = useState('dossier');

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-slate-200 font-sans flex flex-col relative overflow-x-hidden selection:bg-indigo-500/30">
      
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 bg-[#060913]/90 backdrop-blur-md border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#212736] border border-slate-700/50 rounded-lg shadow-[0_0_15px_rgba(99,102,241,0.15)]">
            <Command size={20} className="text-indigo-400" />
          </div>
          <div>
            <div className="text-xl font-black tracking-[0.2em] text-white uppercase leading-none">FRONTIER</div>
            <div className="text-[9px] text-slate-500 font-mono tracking-widest uppercase mt-1">Strategy &amp; Agents</div>
          </div>
        </div>
        
        <div className="hidden md:flex items-center bg-[#0a0f1a] p-1 rounded-xl border border-slate-800 shadow-inner">
           <button 
             onClick={() => setActiveTab('landscape')} 
             className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
               activeTab === 'landscape' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'
             }`}
           >
             01-08 / Main Landscape
           </button>
           <button 
             onClick={() => setActiveTab('dossier')} 
             className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 ${
               activeTab === 'dossier' ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.3)]' : 'text-slate-500 hover:text-slate-300'
             }`}
           >
             <Globe className="w-3.5 h-3.5" /> 09 / Allied Market Entry
           </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 w-full max-w-6xl mx-auto p-6 md:p-10 lg:p-12 relative z-10 pt-10">
        
        {/* Dossier Header */}
        <div className="border-b border-slate-800 pb-8 mb-10">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-indigo-950/40 border border-indigo-500/30 text-[10px] font-mono text-indigo-400 font-bold tracking-widest uppercase mb-4">
                <Shield className="w-3.5 h-3.5" /> Strategic Advisory Dossier
              </div>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-white tracking-tight mb-3">
                Allied Market Entry &amp; Alignment
              </h1>
              <p className="text-slate-400 text-lg max-w-3xl leading-relaxed">
                A board-level diagnostic mapping East Asian industrial capacity against U.S. compliance boundaries and Defense Tech 2.0 interoperability requirements.
              </p>
            </div>
            
            <div className="shrink-0">
               <div className="bg-[#111827] border border-slate-800 rounded-xl p-5 shadow-lg min-w-[250px]">
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Target Profile</div>
                  <h3 className="text-xl font-bold text-white mb-4">LIG Defense &amp; Aerospace</h3>
                  <div className="space-y-3 pt-3 border-t border-slate-800/60">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-400">Jurisdiction</span>
                      <span className="font-mono font-bold text-slate-200">KR (South Korea)</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-400">Classification</span>
                      <span className="font-mono font-bold text-slate-200">Allied Legacy Prime</span>
                    </div>
                  </div>
               </div>
            </div>
          </div>
        </div>

        <div className="space-y-16">
          
          {/* SECTION 1: Executive Architecture */}
          <section className="space-y-6 animate-fade-in-up">
            <div>
              <div className="text-xs font-mono text-indigo-400 font-bold tracking-[0.2em] uppercase mb-2">01 // Architecture</div>
              <h2 className="text-3xl font-bold text-slate-100">The Allied Gap Inversion</h2>
              <p className="text-slate-400 mt-3 text-base leading-relaxed max-w-4xl">
                {renderWithJargon("A critical error made by foreign industrials entering the U.S. defense market is assuming that exquisite hardware capacity (Defense 1.0/1.5) translates to immediate U.S. DoD procurement. Under the Defense Tech 2.0 paradigm, value has migrated upward. For LIG Defense & Aerospace, this creates a structural gap inversion where their domestic hardware mastery hits a regulatory and software wall.")}
              </p>
            </div>

            <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-10 relative overflow-hidden shadow-xl">
              <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />
              
              <div className="flex flex-col lg:flex-row items-stretch gap-8 relative z-10">
                
                {/* Left: Domestic Capacity */}
                <div className="flex-1 space-y-3">
                  <div className="text-center mb-6">
                    <span className="inline-block px-3 py-1 bg-slate-900 border border-slate-700 rounded-md text-[10px] font-mono text-slate-400 font-bold tracking-widest uppercase">
                      Domestic Industrial Foundry (Seoul)
                    </span>
                  </div>
                  
                  <div className="bg-indigo-950/20 border border-indigo-500/20 rounded-xl p-4 flex items-center gap-4">
                    <BrainCircuit className="w-5 h-5 text-indigo-400 shrink-0" />
                    <div>
                      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-0.5">Layer 5</div>
                      <div className="text-sm font-semibold text-slate-300">Fire-Control &amp; Naval C2 Systems (1.5 Legacy)</div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 flex items-center gap-4 opacity-70">
                    <Network className="w-5 h-5 text-slate-500 shrink-0" />
                    <div>
                      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-0.5">Layer 4</div>
                      <div className="text-sm font-semibold text-slate-400">Siloed, Proprietary Architectures</div>
                    </div>
                  </div>

                  <div className="bg-teal-950/20 border border-teal-500/20 rounded-xl p-4 flex items-center gap-4">
                    <Radio className="w-5 h-5 text-teal-400 shrink-0" />
                    <div>
                      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-0.5">Layer 3</div>
                      <div className="text-sm font-semibold text-slate-300">Airborne/Shipborne EW Pods</div>
                    </div>
                  </div>

                  <div className="bg-emerald-950/20 border border-emerald-500/20 rounded-xl p-4 flex items-center gap-4">
                    <Cpu className="w-5 h-5 text-emerald-400 shrink-0" />
                    <div>
                      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-0.5">Layer 2</div>
                      <div className="text-sm font-semibold text-slate-300">Ruggedized Seekers &amp; Analog Avionics</div>
                    </div>
                  </div>

                  <div className="bg-slate-800/40 border border-slate-600 rounded-xl p-4 flex items-center gap-4">
                    <Rocket className="w-5 h-5 text-slate-300 shrink-0" />
                    <div>
                      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-0.5">Layer 1</div>
                      <div className="text-sm font-semibold text-white">Guided Munitions &amp; Rocket Motors</div>
                    </div>
                  </div>
                </div>

                {/* Middle: The Wall */}
                <div className="flex flex-row lg:flex-col items-center justify-center shrink-0 py-4 lg:py-0">
                  <div className="hidden lg:block w-px h-full bg-gradient-to-b from-transparent via-rose-500/50 to-transparent absolute left-1/2 -translate-x-1/2" />
                  <div className="hidden lg:block w-full h-px bg-gradient-to-r from-transparent via-rose-500/50 to-transparent absolute top-1/2 -translate-y-1/2" />
                  
                  <div className="bg-[#0a0f1a] border border-rose-500/40 p-4 rounded-xl shadow-[0_0_20px_rgba(225,29,72,0.15)] flex flex-col items-center text-center relative z-10">
                    <AlertTriangle className="w-6 h-6 text-rose-500 mb-2" />
                    <span className="text-[11px] font-bold text-rose-400 uppercase tracking-widest mb-1">The GRC Wall</span>
                    <span className="text-[9px] font-mono text-slate-400 uppercase">Non-DFARS / BAA Blocked</span>
                  </div>
                </div>

                {/* Right: US Strategy */}
                <div className="flex-1 space-y-3">
                  <div className="text-center mb-6">
                    <span className="inline-block px-3 py-1 bg-slate-900 border border-slate-700 rounded-md text-[10px] font-mono text-slate-400 font-bold tracking-widest uppercase">
                      Compliant U.S. Landing Zone
                    </span>
                  </div>

                  <div className="bg-[#0a0f1a] border border-rose-900/50 rounded-xl p-4 flex items-center gap-4 relative overflow-hidden group">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-rose-500/50" />
                    <BrainCircuit className="w-5 h-5 text-rose-400 shrink-0" />
                    <div>
                      <div className="text-[10px] font-mono text-rose-400/80 uppercase tracking-widest mb-0.5">L5 Acquisition Blocked</div>
                      <div className="text-sm font-semibold text-slate-400 line-through decoration-rose-500/50">Acquire U.S. Agentic AI</div>
                      <div className="text-[10px] text-slate-500 mt-1">{renderWithJargon("CFIUS Block: Cannot acquire classified US AI.")}</div>
                    </div>
                  </div>

                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4 flex items-center gap-4 relative overflow-hidden shadow-sm">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />
                    <Network className="w-5 h-5 text-blue-400 shrink-0" />
                    <div>
                      <div className="text-[10px] font-mono text-blue-400 uppercase tracking-widest mb-0.5">L4 Strategic Alliance</div>
                      <div className="text-sm font-bold text-white">License Certified Software Wrappers</div>
                      <div className="text-[10px] text-slate-400 mt-1">{renderWithJargon("Partner to bridge JADC2 interoperability.")}</div>
                    </div>
                  </div>

                  <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 flex items-center gap-4 opacity-50">
                    <Radio className="w-5 h-5 text-slate-500 shrink-0" />
                    <div>
                      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-0.5">Layer 3 Invisible</div>
                      <div className="text-sm font-semibold text-slate-400">Non-BAA Compliant Systems</div>
                    </div>
                  </div>

                  <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 flex items-center gap-4 opacity-50">
                    <Cpu className="w-5 h-5 text-slate-500 shrink-0" />
                    <div>
                      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-0.5">Layer 2 Invisible</div>
                      <div className="text-sm font-semibold text-slate-400">Non-BAA Compliant Components</div>
                    </div>
                  </div>

                  <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-xl p-4 flex items-center gap-4 relative overflow-hidden shadow-sm">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500" />
                    <Rocket className="w-5 h-5 text-indigo-400 shrink-0" />
                    <div>
                      <div className="text-[10px] font-mono text-indigo-400 uppercase tracking-widest mb-0.5">L1 Onshore Acquisition</div>
                      <div className="text-sm font-bold text-white">Acquire BAA-Compliant US Shell</div>
                      <div className="text-[10px] text-slate-400 mt-1">{renderWithJargon("Establishes FOCI-mitigated manufacturing footprint.")}</div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* SECTION 2: Priority Validation (Tab 01 Matrix) */}
          <section className="space-y-6 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
            <div>
              <div className="text-xs font-mono text-indigo-400 font-bold tracking-[0.2em] uppercase mb-2">02 // Priority Alignment</div>
              <h2 className="text-3xl font-bold text-slate-100">High Conviction Opportunities</h2>
              <p className="text-slate-400 mt-3 text-base leading-relaxed max-w-4xl">
                By filtering the twenty segments of our proprietary <strong className="text-slate-200">Segment Dynamics</strong> database against LIG's industrial capabilities, we isolate the three highest-conviction entry vectors for their strategic pipeline.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Vector 1 */}
              <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 hover:border-slate-700 transition-colors flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-slate-800 text-slate-300">
                    <Rocket className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-0.5">Vector 1 (Layer 1)</div>
                    <h3 className="text-base font-bold text-white leading-tight">C-UAS &amp; Fixed-Site Interception</h3>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2 py-0.5 bg-blue-900/40 border border-blue-500/40 text-blue-300 text-[9px] font-mono font-bold tracking-widest uppercase rounded flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" /> High Conviction
                  </span>
                </div>

                <div className="space-y-4 flex-1">
                  <div>
                    <strong className="text-xs font-semibold text-slate-400 block mb-1">The Catalyst</strong>
                    <p className="text-sm text-slate-300 leading-relaxed">{renderWithJargon("Driven by the Pentagon's Replicator 2.0 mandate. Massive demand for low-cost, high-volume physical mounts and kinetic effectors.")}</p>
                  </div>
                  <div>
                    <strong className="text-xs font-semibold text-slate-400 block mb-1">2.0 Integration Hook</strong>
                    <p className="text-sm text-slate-300 leading-relaxed">LIG's high-rate manufacturing matches a critical U.S. bottleneck, which is starved of scalable "attritable mass" hardware.</p>
                  </div>
                </div>
              </div>

              {/* Vector 2 */}
              <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 hover:border-slate-700 transition-colors flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-blue-900/20 border border-blue-500/20 text-blue-400">
                    <Network className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-0.5">Vector 2 (Layer 4)</div>
                    <h3 className="text-base font-bold text-white leading-tight">JADC2 Data Fusion &amp; Tactical OS</h3>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2 py-0.5 bg-blue-900/40 border border-blue-500/40 text-blue-300 text-[9px] font-mono font-bold tracking-widest uppercase rounded flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" /> High Conviction
                  </span>
                </div>

                <div className="space-y-4 flex-1">
                  <div>
                    <strong className="text-xs font-semibold text-slate-400 block mb-1">The Catalyst</strong>
                    <p className="text-sm text-slate-300 leading-relaxed">The central connective tissue of multi-domain warfare. Commands premium technology multiples (20x-30x Forward Revenue).</p>
                  </div>
                  <div>
                    <strong className="text-xs font-semibold text-slate-400 block mb-1">2.0 Integration Hook</strong>
                    <p className="text-sm text-slate-300 leading-relaxed">To participate in U.S. deployments, LIG’s hardware must be wrapped in open-architecture, containerized operating systems.</p>
                  </div>
                </div>
              </div>

              {/* Vector 3 */}
              <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 hover:border-slate-700 transition-colors flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-emerald-900/20 border border-emerald-500/20 text-emerald-400">
                    <Cpu className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-0.5">Vector 3 (Layer 2)</div>
                    <h3 className="text-base font-bold text-white leading-tight">Edge AI Hardware &amp; Space Payloads</h3>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2 py-0.5 bg-emerald-900/40 border border-emerald-500/40 text-emerald-300 text-[9px] font-mono font-bold tracking-widest uppercase rounded flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> High Conviction
                  </span>
                </div>

                <div className="space-y-4 flex-1">
                  <div>
                    <strong className="text-xs font-semibold text-slate-400 block mb-1">The Catalyst</strong>
                    <p className="text-sm text-slate-300 leading-relaxed">Rad-hardened edge computing. Essential for running AI algorithms at the frontline without relying on jam-vulnerable cloud links.</p>
                  </div>
                  <div>
                    <strong className="text-xs font-semibold text-slate-400 block mb-1">2.0 Integration Hook</strong>
                    <p className="text-sm text-slate-300 leading-relaxed">LIG can integrate their domestic electro-optical sensors into U.S. ruggedized edge GPUs, bypassing heavy ITAR friction.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 3: Compliance Framework */}
          <section className="space-y-6 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
            <div>
              <div className="text-xs font-mono text-indigo-400 font-bold tracking-[0.2em] uppercase mb-2">03 // Compliance Walls</div>
              <h2 className="text-3xl font-bold text-slate-100">Sovereign GRC Readiness</h2>
              <p className="text-slate-400 mt-3 text-base leading-relaxed max-w-4xl">
                A critical regulatory distinction exists under U.S. trade guidelines between East Asian allies, which directly dictates their required U.S. market-entry footprint and localization strategy.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Japan (DFARS) */}
              <div className="bg-[#111827] border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-colors shadow-lg">
                <div className="p-6 md:p-8 border-b border-slate-800 bg-emerald-950/10">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">🇯🇵</span>
                    <h3 className="text-xl font-bold text-white">The Japanese Pathway</h3>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-emerald-900/30 border border-emerald-500/30 text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-widest">
                    <CheckCircle2 className="w-3 h-3" /> DFARS Waived
                  </div>
                </div>
                <div className="p-6 md:p-8">
                  <p className="text-sm text-slate-300 leading-relaxed mb-6">
                    {renderWithJargon("Japan holds official DFARS Qualifying Country status. The Buy American Act (BAA) can be waived for Japanese components.")}
                  </p>
                  <div className="bg-[#0a0f1a] border border-slate-800 p-4 rounded-xl">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Strategic Execution</div>
                    <div className="text-sm text-slate-300 font-medium leading-relaxed">
                      Japanese firms do not require onshore factories; they can act as Tier 2 hardware/material suppliers directly to U.S. Neo-Primes from their domestic lines.
                    </div>
                  </div>
                </div>
              </div>

              {/* South Korea (BAA) */}
              <div className="bg-[#111827] border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-colors shadow-lg">
                <div className="p-6 md:p-8 border-b border-slate-800 bg-rose-950/10">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">🇰🇷</span>
                    <h3 className="text-xl font-bold text-white">The South Korean Pathway</h3>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-rose-900/30 border border-rose-500/30 text-[10px] font-mono font-bold text-rose-400 uppercase tracking-widest">
                    <AlertTriangle className="w-3 h-3" /> BAA Restricted
                  </div>
                </div>
                <div className="p-6 md:p-8">
                  <p className="text-sm text-slate-300 leading-relaxed mb-6">
                    {renderWithJargon("South Korea is currently a non-DFARS country. Under the BAA, completed physical systems face a strict, legally mandated 65% domestic-content threshold.")}
                  </p>
                  <div className="bg-[#0a0f1a] border border-slate-800 p-4 rounded-xl">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Strategic Execution</div>
                    <div className="text-sm text-slate-300 font-medium leading-relaxed">
                      {renderWithJargon("South Korean primes must execute onshore acquisitions to establish a FOCI-mitigated, CMMC Level 2 compliant U.S. manufacturing footprint.")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 4: Matchmaking Sourcing */}
          <section className="space-y-6 animate-fade-in-up" style={{ animationDelay: '300ms' }}>
            <div>
              <div className="text-xs font-mono text-indigo-400 font-bold tracking-[0.2em] uppercase mb-2">04 // Sourcing Pipeline</div>
              <h2 className="text-3xl font-bold text-slate-100">Target Matchmaking &amp; Alliance Roadmap</h2>
              <p className="text-slate-400 mt-3 text-base leading-relaxed max-w-4xl">
                To successfully cross the GRC wall, LIG D&A utilizes a dual-track partnership strategy. We map their capital allocation profile to active U.S. Growth Challengers to establish compliant, high-synergy market pathways.
              </p>
            </div>

            <div className="bg-[#111827] border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-colors shadow-lg">
              <div className="p-6 md:p-8 border-b border-slate-800 bg-slate-900/40">
                <div className="text-[10px] font-mono font-bold text-indigo-400 uppercase tracking-widest flex items-center gap-2">
                  <GitMerge className="w-3.5 h-3.5" /> Dual-Track Strategic Execution
                </div>
              </div>
              
              <div className="p-6 md:p-8">
                <div className="relative">
                  {/* Connecting Line (Desktop) */}
                  <div className="hidden md:block absolute left-1/2 top-8 bottom-8 w-px bg-slate-800/80 -translate-x-1/2" />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 relative z-10">
                    
                    {/* Track 1: Layer 1 Acquisition */}
                    <div className="bg-[#0a0f1a] border border-slate-800 rounded-xl p-6 relative overflow-hidden group hover:border-indigo-500/30 transition-colors shadow-sm">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-bl-full -z-10" />
                      
                      <div className="flex items-start justify-between mb-4 border-b border-slate-800/60 pb-4">
                        <div>
                          <div className="text-[10px] font-mono text-indigo-400 font-bold uppercase tracking-widest mb-1 flex items-center gap-1.5"><Building className="w-3 h-3"/> Track 1: Hardware</div>
                          <h4 className="font-bold text-white text-lg">The "Trojan Horse" Shell</h4>
                        </div>
                        <div className="p-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-400">
                          <Rocket className="w-4 h-4" />
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Execution Mechanism</span>
                          <div className="text-sm font-bold text-white">Onshore Acquisition (Layer 1)</div>
                          <p className="text-xs text-slate-400 mt-1">Bypasses GRC trade barriers by securing an onshore BAA-compliant facility.</p>
                        </div>
                        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                          <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest block mb-2 flex items-center gap-1.5"><Target className="w-3 h-3 text-indigo-400" /> Target Asset (M&A)</span>
                          <div className="font-bold text-indigo-300 text-base mb-1">Ghost Robotics</div>
                          <div className="text-xs font-mono text-slate-400">Philadelphia, PA | 60% Stake ($240M)</div>
                          <p className="text-xs text-slate-300 mt-3 leading-relaxed">
                            LIG's acquisition establishes a FOCI-mitigated, cleared vehicle. This enables them to package their high-quality domestic electronics and sensors inside a BAA-compliant physical platform.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Track 2: Layer 4 Alliance */}
                    <div className="bg-[#0a0f1a] border border-slate-800 rounded-xl p-6 relative overflow-hidden group hover:border-blue-500/30 transition-colors shadow-sm">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-bl-full -z-10" />
                      
                      <div className="flex items-start justify-between mb-4 border-b border-slate-800/60 pb-4">
                        <div>
                          <div className="text-[10px] font-mono text-blue-400 font-bold uppercase tracking-widest mb-1 flex items-center gap-1.5"><BrainCircuit className="w-3 h-3"/> Track 2: Software</div>
                          <h4 className="font-bold text-white text-lg">The "Brain Swap"</h4>
                        </div>
                        <div className="p-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-400">
                          <Network className="w-4 h-4" />
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Execution Mechanism</span>
                          <div className="text-sm font-bold text-white">Strategic Joint Venture (Layer 4/5)</div>
                          <p className="text-xs text-slate-400 mt-1">Bridges the 2.0 software deficit while avoiding insurmountable CFIUS blocks.</p>
                        </div>
                        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                          <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest block mb-2 flex items-center gap-1.5"><Target className="w-3 h-3 text-blue-400" /> Partner Asset (MOU)</span>
                          <div className="font-bold text-blue-300 text-base mb-1">Palantir Technologies</div>
                          <div className="text-xs font-mono text-slate-400">Technology Integration Alliance</div>
                          <p className="text-xs text-slate-300 mt-3 leading-relaxed">
                            Integrates LIG's physical airspace defense interceptors with U.S.-accredited AI targeting suites. Instantly positions their hardware as an interoperable, smart system ready for export.
                          </p>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* Sourcing Pipeline Table (Tab 05 Linkage) */}
            <div className="mt-8 bg-[#111827] border border-slate-800 rounded-2xl overflow-hidden shadow-lg">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-900/40">
                <h3 className="text-lg font-bold text-white">Active Sourcing Pipeline</h3>
                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Cross-Referenced with Tab 05</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-900/80 text-slate-500 font-mono text-[10px] uppercase tracking-wider">
                      <th className="py-4 px-6 border-b border-slate-800">Priority Vector</th>
                      <th className="py-4 px-6 border-b border-slate-800">Execution Strategy</th>
                      <th className="py-4 px-6 border-b border-slate-800">Frontier Target Matches</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    <tr className="hover:bg-slate-800/30 transition-colors group">
                      <td className="py-5 px-6 align-top">
                        <div className="font-bold text-slate-200 mb-1">C-UAS &amp; Fixed-Site Interception</div>
                        <div className="text-xs text-slate-500 font-mono">Layer 1: Physical Edge</div>
                      </td>
                      <td className="py-5 px-6 align-top">
                        <span className="px-2.5 py-1 bg-indigo-900/30 border border-indigo-500/30 text-indigo-400 text-[10px] font-bold rounded block w-fit mb-2">Onshore Shell M&A</span>
                        <p className="text-xs text-slate-400 leading-relaxed">Package Korean rocket seekers inside U.S. automated gun turrets.</p>
                      </td>
                      <td className="py-5 px-6 align-top">
                        <div className="space-y-3">
                          <div className="font-semibold text-slate-300 flex items-center gap-2.5 group-hover:text-indigo-300 transition-colors">
                            <img src="https://www.google.com/s2/favicons?domain=allencontrolsystems.com&sz=64" alt="ACS" className="w-5 h-5 rounded-md bg-slate-800 ring-1 ring-slate-700/50" /> 
                            Allen Control Systems (ACS)
                          </div>
                          <div className="font-semibold text-slate-300 flex items-center gap-2.5 group-hover:text-indigo-300 transition-colors">
                            <img src="https://www.google.com/s2/favicons?domain=fortemtech.com&sz=64" alt="Fortem" className="w-5 h-5 rounded-md bg-slate-800 ring-1 ring-slate-700/50" /> 
                            Fortem Technologies
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-slate-800/30 transition-colors group">
                      <td className="py-5 px-6 align-top">
                        <div className="font-bold text-slate-200 mb-1">JADC2 Data Fusion &amp; OS</div>
                        <div className="text-xs text-slate-500 font-mono">Layer 4: Interoperability</div>
                      </td>
                      <td className="py-5 px-6 align-top">
                        <span className="px-2.5 py-1 bg-blue-900/30 border border-blue-500/30 text-blue-400 text-[10px] font-bold rounded block w-fit mb-2">Strategic JV / MOU</span>
                        <p className="text-xs text-slate-400 leading-relaxed">Wrap Korean hardware in U.S. IL5/IL6 accredited middleware.</p>
                      </td>
                      <td className="py-5 px-6 align-top">
                        <div className="space-y-3">
                          <div className="font-semibold text-slate-300 flex items-center gap-2.5 group-hover:text-blue-300 transition-colors">
                            <img src="https://www.google.com/s2/favicons?domain=secondfront.com&sz=64" alt="Second Front" className="w-5 h-5 rounded-md bg-slate-800 ring-1 ring-slate-700/50" /> 
                            Second Front Systems
                          </div>
                          <div className="font-semibold text-slate-300 flex items-center gap-2.5 group-hover:text-blue-300 transition-colors">
                            <img src="https://www.google.com/s2/favicons?domain=defenseunicorns.com&sz=64" alt="Defense Unicorns" className="w-5 h-5 rounded-md bg-slate-800 ring-1 ring-slate-700/50" /> 
                            Defense Unicorns
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-slate-800/30 transition-colors group">
                      <td className="py-5 px-6 align-top">
                        <div className="font-bold text-slate-200 mb-1">Edge AI Hardware &amp; Payload</div>
                        <div className="text-xs text-slate-500 font-mono">Layer 2: Compute</div>
                      </td>
                      <td className="py-5 px-6 align-top">
                        <span className="px-2.5 py-1 bg-emerald-900/30 border border-emerald-500/30 text-emerald-400 text-[10px] font-bold rounded block w-fit mb-2">Co-Production Partner</span>
                        <p className="text-xs text-slate-400 leading-relaxed">Tier 2 supply of optics into U.S. rad-hardened edge GPUs.</p>
                      </td>
                      <td className="py-5 px-6 align-top">
                        <div className="space-y-3">
                          <div className="font-semibold text-slate-300 flex items-center gap-2.5 group-hover:text-emerald-300 transition-colors">
                            <img src="https://www.google.com/s2/favicons?domain=aethero.space&sz=64" alt="Aethero" className="w-5 h-5 rounded-md bg-slate-800 ring-1 ring-slate-700/50" /> 
                            Aethero
                          </div>
                          <div className="font-semibold text-slate-300 flex items-center gap-2.5 group-hover:text-emerald-300 transition-colors">
                            <img src="https://www.google.com/s2/favicons?domain=takeme2space.com&sz=64" alt="TakeMe2Space" className="w-5 h-5 rounded-md bg-slate-800 ring-1 ring-slate-700/50" /> 
                            TakeMe2Space
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-[#060913] py-8 text-center mt-12">
         <div className="flex items-center justify-center gap-2 text-slate-600 font-mono text-[10px] uppercase tracking-widest">
           <Command className="w-3.5 h-3.5" /> Frontier Strategy &amp; Agents
         </div>
      </footer>
    </div>
  );
}